// =====================================================
// Swiss Finance OS - Realtime Service
// Supabase Realtime for dashboard updates
// =====================================================

import { supabase, getCurrentUserOrganization } from '@/integrations/supabase/client';
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

export type TransactionProcessedData = {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE' | string;
  record: Record<string, unknown>;
  changes?: {
    old: Record<string, unknown> | null;
    new: Record<string, unknown> | null;
  };
};

type FileUploadStatusData = {
  uploadId: string;
  status: string;
  progress: number;
  fileName: string;
  eventType: string;
};

export type BookingEventData = {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE' | string;
  bookingId: string;
  entryNumber?: string;
  status: 'draft' | 'posted' | 'cancelled';
  amount: number;
  description?: string;
};

export type RealtimeUpdate =
  | {
      type: 'transaction_processed';
      data: TransactionProcessedData;
      timestamp: string;
      organizationId: string;
      userId?: string;
    }
  | {
      type: 'booking_event';
      data: BookingEventData;
      timestamp: string;
      organizationId: string;
      userId?: string;
    }
  | {
      type: 'compliance_alert';
      data: ComplianceAlert;
      timestamp: string;
      organizationId: string;
      userId?: string;
    }
  | {
      type: 'processing_status';
      data: ProcessingStatus;
      timestamp: string;
      organizationId: string;
      userId?: string;
    }
  | {
      type: 'team_activity';
      data: TeamActivity;
      timestamp: string;
      organizationId: string;
      userId?: string;
    }
  | {
      type: 'file_upload_status';
      data: FileUploadStatusData;
      timestamp: string;
      organizationId: string;
      userId?: string;
    };

export interface ProcessingStatus {
  batchId: string;
  progress: number;
  status: 'processing' | 'completed' | 'error' | 'paused';
  currentFile?: string;
  totalFiles: number;
  processedFiles: number;
  estimatedTimeRemaining?: number;
}

export interface ComplianceAlert {
  type: 'vat_deadline' | 'audit_required' | 'missing_documents' | 'anomaly_detected';
  severity: 'low' | 'medium' | 'high' | 'urgent';
  title: string;
  message: string;
  mandateId?: string;
  clientName?: string;
  deadline?: string;
  actionRequired?: boolean;
}

export interface TeamActivity {
  userId: string;
  userName: string;
  action: 'file_uploaded' | 'transaction_validated' | 'export_completed' | 'login' | 'logout';
  details: string;
  timestamp: string;
}

class RealtimeService {
  private channels: Map<string, RealtimeChannel> = new Map();
  private eventListeners: Map<string, Set<(update: RealtimeUpdate) => void>> = new Map();
  private isConnected = false;
  private currentOrganizationId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private isQuotaExceeded = false;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;

  async initialize(): Promise<void> {
    // Don't initialize if quota is exceeded
    if (this.isQuotaExceeded) {
      console.warn('Realtime service disabled: API quota exceeded');
      return;
    }

    const userOrg = await getCurrentUserOrganization();
    if (!userOrg) {
      // Don't warn on public pages - this is expected behavior
      this.isConnected = false;
      return;
    }

    this.currentOrganizationId = userOrg.company_id;
    await this.setupChannels();
    this.isConnected = true;
    this.reconnectAttempts = 0; // Reset on successful connection

    console.log(`Realtime service initialized for organization: ${this.currentOrganizationId}`);
  }

  private async setupChannels(): Promise<void> {
    if (!this.currentOrganizationId) return;

    // Channel for transaction processing updates
    this.setupTransactionChannel();

    // Channel for booking events
    this.setupBookingChannel();

    // Channel for file upload status
    this.setupFileUploadChannel();

    // Channel for compliance alerts
    this.setupComplianceChannel();

    // Channel for team activity
    this.setupTeamActivityChannel();
  }

  private setupTransactionChannel(): void {
    const channel = supabase.channel(`transactions:${this.currentOrganizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
          filter: `organization_id=eq.${this.currentOrganizationId}`
        },
        (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
          this.handleTransactionUpdate(payload);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'file_uploads',
          filter: `organization_id=eq.${this.currentOrganizationId}`
        },
        (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
          this.handleFileUploadUpdate(payload);
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log('Transaction realtime channel subscribed');
        } else if (status === 'CHANNEL_ERROR') {
          this.handleChannelError(err);
        } else if (status === 'TIMED_OUT') {
          this.handleReconnect();
        } else if (status === 'CLOSED') {
          console.log('Transaction channel closed');
        }
      });

    this.channels.set('transactions', channel);
  }

  private setupBookingChannel(): void {
    const channel = supabase.channel(`bookings:${this.currentOrganizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'journal_entries'
        },
        (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
          this.handleBookingUpdate(payload);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'booking_entries'
        },
        (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
          this.handleBookingEntryUpdate(payload);
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log('Booking realtime channel subscribed');
        } else if (status === 'CHANNEL_ERROR') {
          this.handleChannelError(err);
        }
      });

    this.channels.set('bookings', channel);
  }

  private setupFileUploadChannel(): void {
    const channel = supabase.channel(`file_uploads:${this.currentOrganizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'file_uploads',
          filter: `organization_id=eq.${this.currentOrganizationId}`
        },
        (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
          this.handleFileUploadStatusUpdate(payload);
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log('File upload realtime channel subscribed');
        } else if (status === 'CHANNEL_ERROR') {
          this.handleChannelError(err);
        }
      });

    this.channels.set('file_uploads', channel);
  }

  private setupComplianceChannel(): void {
    const channel = supabase.channel(`compliance:${this.currentOrganizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'compliance_alerts',
          filter: `organization_id=eq.${this.currentOrganizationId}`
        },
        (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
          this.handleComplianceUpdate(payload);
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log('Compliance realtime channel subscribed');
        } else if (status === 'CHANNEL_ERROR') {
          this.handleChannelError(err);
        }
      });

    this.channels.set('compliance', channel);
  }

  private setupTeamActivityChannel(): void {
    const channel = supabase.channel(`team_activity:${this.currentOrganizationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'audit_logs',
          filter: `organization_id=eq.${this.currentOrganizationId}`
        },
        (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
          this.handleTeamActivityUpdate(payload);
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log('Team activity realtime channel subscribed');
        } else if (status === 'CHANNEL_ERROR') {
          this.handleChannelError(err);
        }
      });

    this.channels.set('team_activity', channel);
  }

  private handleTransactionUpdate(payload: RealtimePostgresChangesPayload<Record<string, unknown>>): void {
    const update: RealtimeUpdate = {
      type: 'transaction_processed',
      data: {
        eventType: payload.eventType,
        record: payload.new || payload.old,
        changes: payload.eventType === 'UPDATE' ? {
          old: payload.old,
          new: payload.new
        } : undefined
      },
      timestamp: new Date().toISOString(),
      organizationId: this.currentOrganizationId!,
    };

    this.emitUpdate(update);
  }

  private handleFileUploadUpdate(payload: RealtimePostgresChangesPayload<Record<string, unknown>>): void {
    const record = payload.new || payload.old;
    if (!record) return;

    const update: RealtimeUpdate = {
      type: 'file_upload_status',
      data: {
        uploadId: (record as any).id,
        status: (record as any).status,
        progress: (record as any).progress || 0,
        fileName: (record as any).file_name,
        eventType: payload.eventType
      },
      timestamp: new Date().toISOString(),
      organizationId: this.currentOrganizationId!,
    };

    this.emitUpdate(update);
  }

  private handleBookingUpdate(payload: RealtimePostgresChangesPayload<Record<string, unknown>>): void {
    const record = payload.new || payload.old;
    const update: RealtimeUpdate = {
      type: 'booking_event',
      data: {
        eventType: payload.eventType,
        bookingId: (record as any)?.id || '',
        entryNumber: (record as any)?.entry_number,
        status: (record as any)?.status || 'draft',
        amount: (record as any)?.amount || 0,
        description: (record as any)?.description,
      },
      timestamp: new Date().toISOString(),
      organizationId: this.currentOrganizationId!,
    };

    this.emitUpdate(update);
  }

  private handleBookingEntryUpdate(payload: RealtimePostgresChangesPayload<Record<string, unknown>>): void {
    // For booking entry updates, we might want to trigger a refresh of the parent booking
    // This is a simplified handler - you might want to enhance it based on your needs
    const record = payload.new || payload.old;
    if ((record as any)?.journal_entry_id) {
      const update: RealtimeUpdate = {
        type: 'booking_event',
        data: {
          eventType: 'UPDATE',
          bookingId: (record as any).journal_entry_id,
          status: 'draft',
          amount: 0,
        },
        timestamp: new Date().toISOString(),
        organizationId: this.currentOrganizationId!,
      };
      
      this.emitUpdate(update);
    }
  }

  private handleFileUploadStatusUpdate(payload: RealtimePostgresChangesPayload<Record<string, unknown>>): void {
    const record = payload.new || payload.old;
    if (!record) return;

    // Create processing status update
    const statusUpdate: ProcessingStatus = {
      batchId: (record as any).batch_id || (record as any).id,
      progress: (record as any).progress || 0,
      status: this.mapStatusToProcessingStatus((record as any).status),
      currentFile: (record as any).file_name,
      totalFiles: (record as any).total_files || 1,
      processedFiles: (record as any).processed_files || 0,
      estimatedTimeRemaining: (record as any).estimated_time_remaining
    };

    const update: RealtimeUpdate = {
      type: 'processing_status',
      data: statusUpdate,
      timestamp: new Date().toISOString(),
      organizationId: this.currentOrganizationId!,
    };

    this.emitUpdate(update);
  }

  private handleComplianceUpdate(payload: RealtimePostgresChangesPayload<Record<string, unknown>>): void {
    const record = payload.new;
    if (!record) return;

    const alert: ComplianceAlert = {
      type: (record as any).alert_type,
      severity: (record as any).severity,
      title: (record as any).title,
      message: (record as any).message,
      mandateId: (record as any).mandate_id,
      clientName: (record as any).client_name,
      deadline: (record as any).deadline,
      actionRequired: (record as any).action_required
    };

    const update: RealtimeUpdate = {
      type: 'compliance_alert',
      data: alert,
      timestamp: new Date().toISOString(),
      organizationId: this.currentOrganizationId!,
    };

    this.emitUpdate(update);
  }

  private handleTeamActivityUpdate(payload: RealtimePostgresChangesPayload<Record<string, unknown>>): void {
    const record = payload.new;
    if (!record) return;

    const activity: TeamActivity = {
      userId: (record as any).user_id,
      userName: (record as any).user_name || 'Unknown User',
      action: this.mapActionToTeamActivity((record as any).action),
      details: (record as any).details || (record as any).table_name,
      timestamp: (record as any).created_at
    };

    const update: RealtimeUpdate = {
      type: 'team_activity',
      data: activity,
      timestamp: new Date().toISOString(),
      organizationId: this.currentOrganizationId!,
    };

    this.emitUpdate(update);
  }

  private mapStatusToProcessingStatus(status: string): ProcessingStatus['status'] {
    switch (status) {
      case 'processing':
      case 'uploading':
        return 'processing';
      case 'completed':
      case 'success':
        return 'completed';
      case 'failed':
      case 'error':
        return 'error';
      case 'paused':
        return 'paused';
      default:
        return 'processing';
    }
  }

  private mapActionToTeamActivity(action: string): TeamActivity['action'] {
    switch (action.toLowerCase()) {
      case 'insert':
      case 'upload':
        return 'file_uploaded';
      case 'update':
      case 'validate':
        return 'transaction_validated';
      case 'export':
        return 'export_completed';
      case 'login':
        return 'login';
      case 'logout':
        return 'logout';
      default:
        return 'file_uploaded';
    }
  }

  private emitUpdate(update: RealtimeUpdate): void {
    const listeners = this.eventListeners.get(update.type);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(update);
        } catch (error) {
          console.error('Error in realtime listener:', error);
        }
      });
    }

    // Also emit to 'all' listeners
    const allListeners = this.eventListeners.get('all');
    if (allListeners) {
      allListeners.forEach(listener => {
        try {
          listener(update as any);
        } catch (error) {
          console.error('Error in realtime all listener:', error);
        }
      });
    }
  }

  // Public API
  subscribe(eventType: RealtimeUpdate['type'] | 'all', listener: (update: RealtimeUpdate) => void): () => void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set());
    }

    this.eventListeners.get(eventType)!.add(listener);

    // Return unsubscribe function
    return () => {
      const listeners = this.eventListeners.get(eventType);
      if (listeners) {
        listeners.delete(listener);
      }
    };
  }

  // Manually emit an update (for testing or custom events)
  emitCustomUpdate(update: Omit<RealtimeUpdate, 'organizationId' | 'timestamp'>): void {
    const fullUpdate: RealtimeUpdate = {
      ...update,
      organizationId: this.currentOrganizationId!,
      timestamp: new Date().toISOString()
    } as RealtimeUpdate;

    this.emitUpdate(fullUpdate);
  }

  // Check connection status
  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  // Cleanup
  async cleanup(): Promise<void> {
    // Clear any pending reconnect attempts
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    for (const [name, channel] of this.channels) {
      await channel.unsubscribe();
      console.log(`Unsubscribed from ${name} channel`);
    }

    this.channels.clear();
    this.eventListeners.clear();
    this.isConnected = false;
    this.currentOrganizationId = null;
  }

  // Handle channel errors (e.g., 402 quota exceeded)
  private handleChannelError(error: any): void {
    console.error('Realtime channel error:', error);
    
    // Check if it's a quota error (402 status)
    const errorMessage = error?.message || String(error);
    if (errorMessage.includes('402') || errorMessage.includes('quota') || errorMessage.includes('limit exceeded')) {
      console.error('🚨 API Quota exceeded - disabling realtime updates');
      this.isQuotaExceeded = true;
      this.isConnected = false;
      this.cleanup();
      return;
    }

    // For other errors, try to reconnect
    this.handleReconnect();
  }

  // Handle reconnection with exponential backoff
  private handleReconnect(): void {
    if (this.isQuotaExceeded) {
      console.warn('Cannot reconnect: API quota exceeded');
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`Max reconnection attempts (${this.maxReconnectAttempts}) reached. Stopping.`);
      this.isConnected = false;
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff
    
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms...`);

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
    }

    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnect();
    }, delay);
  }

  // Reconnect
  async reconnect(): Promise<void> {
    if (this.isQuotaExceeded) {
      console.warn('Reconnect blocked: API quota exceeded');
      return;
    }
    
    await this.cleanup();
    await this.initialize();
  }

  // Check if quota is exceeded
  isQuotaError(): boolean {
    return this.isQuotaExceeded;
  }

  // Reset quota error state (call this after user upgrades plan)
  resetQuotaError(): void {
    this.isQuotaExceeded = false;
    this.reconnectAttempts = 0;
    console.log('Quota error state reset - realtime can reconnect');
  }
}

// Singleton instance
export const realtimeService = new RealtimeService();

// Export hook for easy use in React components
export { default as useRealtimeUpdates } from '@/hooks/use-realtime-updates';
